// Cards and stat tiles — the containers a page is built from.

import type { HTMLAttributes, ReactNode } from "react";
import { cx } from "@/platform/ui/primitives/cx.js";

// ---- Card ----
export function Card({
  children,
  className,
  padded = true,
  ...rest
}: HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <div
      className={cx("rounded-xl border border-line bg-surface", padded && "p-4 sm:p-5", className)}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardTitle({
  children,
  sub,
  actions,
}: {
  children: ReactNode;
  /** Small muted text/badge, inline right after the title. */
  sub?: ReactNode;
  /** Right-aligned controls: buttons, toggles, filters, legends. */
  actions?: ReactNode;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          <h3 className="text-sm font-bold tracking-tight text-ink">{children}</h3>
          {sub && <span className="text-xs text-muted">{sub}</span>}
        </div>
      </div>
      {actions && (
        <div className="flex max-w-full flex-none flex-wrap items-center gap-2">{actions}</div>
      )}
    </div>
  );
}

// ---- StatTile ----
/** The small heading over one section of a modal or an editor. */
export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <h3 className="text-[11px] font-extrabold uppercase tracking-wider text-muted">{children}</h3>
  );
}

export function StatTile({
  label,
  value,
  hint,
  tone = "default",
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: "default" | "positive" | "negative" | "warning" | "accent";
}) {
  const toneClass = {
    default: "text-ink",
    positive: "text-positive",
    negative: "text-negative",
    warning: "text-warning",
    accent: "text-accent",
  }[tone];
  return (
    <Card className="min-w-0">
      <div className="text-xs font-medium text-muted">{label}</div>
      <div className={cx("mono mt-2 text-2xl font-bold tracking-tight", toneClass)}>{value}</div>
      {hint && <div className="mt-1 text-xs text-faint">{hint}</div>}
    </Card>
  );
}
